import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import Emblem from "./Emblem";
import { useTheme } from "./ThemeController";

const NAV_ITEMS = [
    { label: "Work", target: "#fleet" },
    { label: "Services", target: "#services" },
    { label: "Case Studies", target: "#work" },
    { label: "Instagram", target: "#instagram" },
    { label: "Praise", target: "#praise" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { theme, toggle } = useTheme();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            data-testid="site-nav"
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: 0.05 }}
            className={`fixed top-0 left-0 right-0 z-50 glass transition-colors duration-500 ${
                scrolled
                    ? "backdrop-blur-2xl bg-black/60 border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <div className="flex items-stretch justify-between px-6 md:px-10 py-4">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                >
                    <Emblem size={34} />
                    <div className="flex flex-col leading-none">
                        <span className="font-display text-lg tracking-tight uppercase">
                            PaintKing
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.28em] text-white/50 uppercase">
                            SOFLA · EST. PAUL
                        </span>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.label}
                            href={item.target}
                            data-testid={`nav-${item.label.toLowerCase()}`}
                            className="font-mono text-xs tracking-[0.24em] uppercase text-white/70 hover:text-white transition-colors relative group"
                        >
                            {item.label}
                            <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full rainbow-bg transition-all duration-500" />
                        </a>
                    ))}
                </nav>

                <a
                    href="tel:5616984044"
                    data-testid="nav-phone-cta"
                    className="hidden md:inline-flex items-center gap-3 glass-btn border border-white/20 hover:border-white px-5 py-2.5 font-mono text-xs tracking-[0.24em] uppercase transition-colors group relative overflow-hidden"
                >
                    <span className="absolute inset-0 rainbow-bg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="relative z-10 flex items-center gap-2 group-hover:text-black">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00FF85] blink" />
                        561·698·4044
                    </span>
                </a>

                {/* Theme toggle — triggers the paint-wipe */}
                <button
                    onClick={toggle}
                    data-testid="theme-toggle"
                    aria-label="Toggle light and dark mode"
                    className="hidden md:inline-flex items-center gap-2 glass-btn border border-white/20 hover:border-white px-4 py-2.5 font-mono text-[10px] tracking-[0.24em] uppercase transition-colors"
                >
                    {theme === "dark" ? (
                        <Sun className="w-4 h-4" strokeWidth={1.75} />
                    ) : (
                        <Moon className="w-4 h-4" strokeWidth={1.75} />
                    )}
                    {theme === "dark" ? "Light" : "Dark"}
                </button>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Menu"
                    className="md:hidden border border-white/20 px-3 py-2 font-mono text-xs tracking-widest uppercase"
                >
                    {open ? "Close" : "Menu"}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="md:hidden overflow-hidden bg-black border-t border-white/10"
                    >
                        <div className="flex flex-col px-6 py-6 gap-4">
                            {NAV_ITEMS.map((item) => (
                                <a
                                    key={item.label}
                                    href={item.target}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                                    className="font-display text-3xl uppercase"
                                >
                                    {item.label}
                                </a>
                            ))}
                            <a
                                href="tel:5616984044"
                                data-testid="nav-mobile-phone"
                                className="mt-4 font-mono text-sm tracking-[0.24em] uppercase text-[#FFE600]"
                            >
                                → 561·698·4044
                            </a>
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    toggle();
                                }}
                                data-testid="theme-toggle-mobile"
                                className="mt-2 inline-flex items-center gap-2 font-mono text-xs tracking-[0.24em] uppercase text-white/70"
                            >
                                {theme === "dark" ? (
                                    <Sun className="w-4 h-4" strokeWidth={1.75} />
                                ) : (
                                    <Moon className="w-4 h-4" strokeWidth={1.75} />
                                )}
                                {theme === "dark" ? "Light mode" : "Dark mode"}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
